/**
 * Winter Impact Prediction (WIP) & Region Hooks
 *
 * WIP (district) flow:
 *   useWIPMetadata → useWIPScenario → useWIPImpacts
 *
 * Region flow:
 *   useRegionMetadata → useRegionImpacts
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { wipApi } from "@/api/services/wip";
import type { ApiError } from "@/api/client";
import type {
  ScenarioParameterValues,
  RegionImpactQueryParams,
} from "@/api/types";

type RetryableApiError = ApiError & {
  response?: {
    status?: number;
  };
};

/** Slider parameter values for district WIP scenarios. */
export function useWIPMetadata(
  stormId: string | null,
  stormApplicationId: string | null,
) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["wip", "parameters", stormId, stormApplicationId, "district"],
    queryFn: () =>
      wipApi.getScenarioParameters(stormId!, stormApplicationId!, "district"),
    enabled: !!stormId && !!stormApplicationId,
    staleTime: Infinity,
  });
  return { data, loading: isLoading, isFetching, error };
}

/** Resolve a shift combination → scenario_metadata_id. */
export function useWIPScenario(
  stormId: string | null,
  stormApplicationId: string | null,
  params: ScenarioParameterValues | null,
) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      "wip",
      "scenario",
      stormId,
      stormApplicationId,
      "district",
      params,
    ],
    queryFn: () =>
      wipApi.matchScenario(stormId!, stormApplicationId!, "district", params!),
    enabled: !!stormId && !!stormApplicationId && !!params,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    retry: (failureCount, err: unknown) => {
      const status =
        (err as RetryableApiError)?.status ??
        (err as RetryableApiError)?.response?.status;
      if (status === 422) return false;
      return failureCount < 2;
    },
  });
  return { data, loading: isLoading, isFetching, error };
}

/** District-level impacts for a resolved WIP scenario. */
export function useWIPImpacts(scenarioMetadataId: string | null) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["wip", "impacts", scenarioMetadataId],
    queryFn: () => wipApi.getImpacts(scenarioMetadataId!),
    enabled: !!scenarioMetadataId,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
  return {
    data: data?.forecasts ?? [],
    scenario: data?.scenario,
    response: data,
    loading: isLoading,
    isFetching,
    error,
  };
}

/** Slider parameter values for region scenarios. */
export function useRegionMetadata(
  stormId: string | null,
  stormApplicationId: string | null,
) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["wip", "parameters", stormId, stormApplicationId, "region"],
    queryFn: () => wipApi.getRegionMetadata(stormId!, stormApplicationId!),
    enabled: !!stormId && !!stormApplicationId,
    staleTime: Infinity,
  });
  return { data, loading: isLoading, isFetching, error };
}

/** Region geometry for a shift combination (latest run). */
export function useRegionImpacts(
  stormId: string | null,
  stormApplicationId: string | null,
  params: RegionImpactQueryParams | null,
) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["wip", "region", "impacts", stormId, stormApplicationId, params],
    queryFn: () => wipApi.getRegionImpacts(stormId!, stormApplicationId!, params!),
    enabled: !!stormId && !!stormApplicationId && !!params,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
  return { data, loading: isLoading, isFetching, error };
}
